import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { CustomIcon } from "components/icon/customIcon.component";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import type { EmploymentTemplateMenuOption } from "../shared/staffEmploymentTemplate.logic";
export interface EmploymentTemplateSelection {
  readonly salaryId: string;
  readonly templateId: string;
}

export interface EmploymentTemplateMenuProps {
  readonly disabled: boolean;
  readonly isLoading: boolean;
  readonly options: EmploymentTemplateMenuOption[];
  readonly onSelect: (selection: EmploymentTemplateSelection) => void;
}

export const EmploymentTemplateMenu = (
  props: EmploymentTemplateMenuProps,
): JSX.Element => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback((): void => {
    setIsOpen(false);
  }, []);

  useOnClickOutside([containerRef], closeMenu, { enabled: isOpen });

  const handleToggle = (): void => {
    if (props.disabled) return;
    setIsOpen((previousIsOpen) => !previousIsOpen);
  };

  const handleSelect = (selection: EmploymentTemplateSelection): void => {
    props.onSelect(selection);
    closeMenu();
  };

  const hasOptions = props.options.length > 0;

  return (
    <Container ref={containerRef} onClick={(event) => event.stopPropagation()}>
      <TriggerButton
        type="button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        disabled={props.disabled}
        $isOpen={isOpen}
        onClick={handleToggle}
      >
        <CustomIcon name="v2/list" size="20px" color={ColorV2.Blue2} />
        <TriggerLabel>{t("Staff.AddStaffPanelFieldTemplate")}</TriggerLabel>
      </TriggerButton>
      <List $isOpen={isOpen} role="menu">
        <ListScroll>
          {props.isLoading && <Message>{t("General.Loading")}</Message>}
          {!props.isLoading && !hasOptions && (
            <Message>{t("General.NoResults")}</Message>
          )}
          {props.options.map((option) => (
            <TemplateBlock key={option.id}>
              <OptionRow
                type="button"
                role="menuitem"
                onClick={() =>
                  handleSelect({ salaryId: "", templateId: option.id })
                }
              >
                <TemplateLabel>{option.name}</TemplateLabel>
              </OptionRow>
              {option.salaries.length > 0 && (
                <SalariesList>
                  {option.salaries.map((salary) => (
                    <OptionRow
                      key={salary.id}
                      type="button"
                      role="menuitem"
                      onClick={() =>
                        handleSelect({
                          salaryId: salary.id,
                          templateId: option.id,
                        })
                      }
                    >
                      <SalaryLabel>{salary.name}</SalaryLabel>
                    </OptionRow>
                  ))}
                </SalariesList>
              )}
            </TemplateBlock>
          ))}
        </ListScroll>
      </List>
    </Container>
  );
};

const TRIGGER_HEIGHT_PX = 36;
const MENU_WIDTH_PX = 340;
const MENU_MAX_HEIGHT_PX = 280;

const Container = styled.div`
  position: relative;
  display: inline-flex;
`;

const TriggerButton = styled.button<{ $isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: ${TRIGGER_HEIGHT_PX}px;
  padding: 0 12px;
  border-radius: 12px;
  border: 1.5px solid
    ${(props) => (props.$isOpen ? ColorV2.Blue1 : ColorV2.Blue5)};
  background-color: ${ColorV2.White};
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${ColorV2.Blue7};
  }

  &:disabled {
    border-color: ${ColorV2.Blue6};
    background-color: ${ColorV2.Blue7};
    cursor: not-allowed;
  }
`;

const TriggerLabel = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue1};
  white-space: nowrap;
`;

const List = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: ${MENU_WIDTH_PX}px;
  padding: 8px 0;
  border-radius: 16px;
  background-color: ${ColorV2.White};
  border: 1.5px solid ${ColorV2.Blue5};
  box-shadow: 0 12px 24px rgba(15, 42, 64, 0.12);
  overflow: hidden;
  z-index: 1;
  display: ${(props) => (props.$isOpen ? "block" : "none")};
`;

const ListScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: ${MENU_MAX_HEIGHT_PX}px;
  overflow-y: auto;
  padding: 8px 12px;
  box-sizing: border-box;

  scrollbar-gutter: stable;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${ColorV2.Blue7};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${ColorV2.Blue6};
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${ColorV2.Blue5};
  }
`;

const TemplateBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SalariesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 16px;
`;

const OptionRow = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 32px;
  padding: 6px 8px;
  border: none;
  border-radius: 8px;
  background-color: transparent;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: ${ColorV2.Blue7};
  }
`;

const labelStyles = css`
  flex: 1;
  min-width: 0;
  color: ${ColorV2.Blue1};
`;

const TemplateLabel = styled.span`
  ${labelStyles}
  ${TypographyV2.b1}
`;

const SalaryLabel = styled.span`
  ${labelStyles}
  ${TypographyV2.s1}
`;

const Message = styled.span`
  ${TypographyV2.s1}
  color: ${ColorV2.Blue4};
  padding: 6px 8px;
`;
